/**
 * Quick Setup Tab
 *
 * Compact target setup with preset frameworks and sliders.
 */

import { Row, Col, Input } from 'reactstrap';
import type { CreateTargetRequest } from '../../types/emissions';
import { TargetFramework } from '../../types/emissions';

interface QuickSetupTabProps {
  formData: CreateTargetRequest;
  onFormChange: (data: Partial<CreateTargetRequest>) => void;
}

const FRAMEWORK_OPTIONS = [
  { value: TargetFramework.SBTi, label: 'Science Based Targets (SBTi)' },
  { value: TargetFramework.NetZero, label: 'Net Zero by 2050' },
  { value: TargetFramework.CarbonNeutral, label: 'Carbon Neutral' },
  { value: TargetFramework.Custom, label: 'Custom Target' },
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => currentYear + i);
const TARGET_YEAR_OPTIONS = Array.from({ length: 31 }, (_, i) => currentYear + i);

export const QuickSetupTab = ({ formData, onFormChange }: QuickSetupTabProps) => {
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFormChange({ reductionPercentage: Number(e.target.value) });
  };

  return (
    <div>
      {/* Target Framework */}
      <div className='mb-3'>
        <label className='form-label fw-medium mb-1'>Target Framework</label>
        <Input
          type='select'
          value={formData.framework}
          onChange={(e) => onFormChange({ framework: e.target.value as TargetFramework })}
        >
          {FRAMEWORK_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>
      </div>

      {/* Reduction Target Slider */}
      <div className='mb-3'>
        <div className='d-flex justify-content-between align-items-center mb-1'>
          <label className='form-label fw-medium mb-0'>Reduction Target</label>
          <span className='fw-bold text-success' style={{ fontSize: '1.25rem' }}>
            {formData.reductionPercentage}%
          </span>
        </div>
        <Input
          type='range'
          min='5'
          max='100'
          step='5'
          value={formData.reductionPercentage}
          onChange={handleSliderChange}
          className='form-range'
          style={{ accentColor: '#198754' }}
        />
        <div className='d-flex justify-content-between text-ThemeTextLight' style={{ fontSize: '0.7rem' }}>
          <span>5%</span>
          <span>50%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Year Selection - Side by Side */}
      <Row className='g-2'>
        <Col xs={6}>
          <label className='form-label fw-medium mb-1'>Baseline Year</label>
          <Input
            type='select'
            value={formData.baselineYear}
            onChange={(e) => onFormChange({ baselineYear: Number(e.target.value) })}
          >
            {YEAR_OPTIONS.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Input>
        </Col>
        <Col xs={6}>
          <label className='form-label fw-medium mb-1'>Target Year</label>
          <Input
            type='select'
            value={formData.targetYear}
            onChange={(e) => onFormChange({ targetYear: Number(e.target.value) })}
          >
            {TARGET_YEAR_OPTIONS.filter((year) => year > formData.baselineYear).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Input>
        </Col>
      </Row>
    </div>
  );
};
